import React, { RefObject } from "react";
import { makeStyles } from "tss-react/mui";

import { DropdownList, DropdownTrigger } from "@devices/components/shared";
import { TooltipProps, Typography } from "@mui/material";

import { DropdownListProps } from "./DropdownList";
import { DropdownTriggerProps } from "./DropdownTrigger";

const useStyles = makeStyles()((theme) => ({
    trigger: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
}));

type Props = React.PropsWithChildren<{
    disableClickAway?: boolean;
    displayLeft?: boolean;
    anchorRef: RefObject<HTMLButtonElement | null>;
    icon?: React.ReactNode;
    disabled?: boolean;
    label?: string;
    tooltipProps?: Omit<TooltipProps, "arrow" | "children">;
    listProps?: Partial<DropdownListProps>;
    triggerProps?: Partial<DropdownTriggerProps>;
    // Renders no trigger button of its own; open state is driven by `open`/`onOpenChange` instead,
    // for menus opened from an entry in a different dropdown that shares the same anchorRef.
    hideTrigger?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}>;

const toogleEvent = "menu:toggle";

export default function DropdownContainer({
    children,
    disableClickAway = false,
    displayLeft = false,
    anchorRef,
    icon,
    disabled = false,
    tooltipProps,
    label,
    listProps,
    triggerProps,
    hideTrigger = false,
    open,
    onOpenChange,
}: Props): React.JSX.Element {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const editMenuOpen = open !== undefined ? open : internalOpen;
    const setEditMenuOpen = React.useCallback(
        (value: boolean) => {
            if (onOpenChange) {
                onOpenChange(value);
            } else {
                setInternalOpen(value);
            }
        },
        [onOpenChange],
    );
    const prevMenuOpenRef = React.useRef(editMenuOpen);
    const { classes } = useStyles();

    const handleToggle = () => {
        if (!editMenuOpen) {
            document.dispatchEvent(new Event(toogleEvent));
        }
        setEditMenuOpen(!editMenuOpen);
    };
    const handleClickAway = () => {
        if (disableClickAway) {
            return;
        }
        setEditMenuOpen(false);
    };

    React.useEffect(() => {
        if (prevMenuOpenRef.current && !editMenuOpen && listProps?.onClickAway) {
            listProps.onClickAway();
        }
        prevMenuOpenRef.current = editMenuOpen;
    }, [editMenuOpen, listProps]);

    React.useEffect(() => {
        const handleMenuToggle = () => {
            if (editMenuOpen) {
                setEditMenuOpen(false);
            }
        };

        document.addEventListener(toogleEvent, handleMenuToggle);
        return () => document.removeEventListener(toogleEvent, handleMenuToggle);
    }, [editMenuOpen, setEditMenuOpen]);

    return (
        <div>
            {hideTrigger ? null : (
                <DropdownTrigger
                    onClick={handleToggle}
                    anchorRef={anchorRef}
                    disabled={disabled}
                    tooltipProps={tooltipProps}
                    data-testid="menu-trigger"
                    className={classes.trigger}
                    {...triggerProps}
                >
                    {icon ? icon : null}
                    {label ? <Typography>{label}</Typography> : null}
                </DropdownTrigger>
            )}
            <DropdownList
                isOpen={editMenuOpen}
                anchorRef={anchorRef}
                displayLeft={displayLeft}
                {...listProps}
                onClickAway={handleClickAway}
            >
                {children}
            </DropdownList>
        </div>
    );
}
